import { c as _c } from "react/compiler-runtime";
import { type FC, type ReactEventHandler, useState } from "react";
import { useTransitionRouter } from "next-view-transitions";
import { track } from "@vercel/analytics";
import posthog from "posthog-js";
import type { HttpError } from "@vita-learning/types";
import { makeHandleHttpError, makePosthogEventName as ph } from "~/shared/services";
import { increment, isEqual, stringFieldsAreFilled, validateEmail } from "~/shared/utils";
import { CancelButton, DeleteButton, PrimaryButton } from "../../buttons";
import { TextFormGroup, useTextFormGroup } from "../../formgroups";
import { requireField } from "../../forms";
import { Alert, useAlert } from "../../portals";
import { useUserStore } from "../../stores";
import "./styles.scss";
export const SettingsProfile: FC = () => {
  const $ = _c(63);
  const router = useTransitionRouter();
  const {
    onCancel,
    onOpen
  } = useAlert();
  const [key, setKey] = useState(0);
  const [error, setError] = useState(null);
  const user = useUserStore(_temp);
  const updateProfile = useUserStore(_temp2);
  const destroyUser = useUserStore(_temp3);
  const t0 = user.name.first;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = requireField("First name");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== error || $[2] !== user.name.first) {
    t2 = {
      label: "First Name",
      "data-testid": "user-first-name-error",
      initialValue: t0,
      serverError: error,
      validate: t1
    };
    $[1] = error;
    $[2] = user.name.first;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const firstNameInput = useTextFormGroup(t2);
  const t3 = user.name.last;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = requireField("Last name");
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== error || $[6] !== user.name.last) {
    t5 = {
      label: "Last name",
      "data-testid": "user-last-name-error",
      initialValue: t3,
      serverError: error,
      validate: t4
    };
    $[5] = error;
    $[6] = user.name.last;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const lastNameInput = useTextFormGroup(t5);
  let t6;
  if ($[8] !== error || $[9] !== user.email) {
    t6 = {
      label: "Email",
      "data-testid": "user-email-error",
      initialValue: user.email,
      type: "email",
      serverError: error,
      validate: validateEmail
    };
    $[8] = error;
    $[9] = user.email;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const emailInput = useTextFormGroup(t6);
  let payload;
  let t7;
  if ($[11] !== emailInput.value || $[12] !== firstNameInput.value || $[13] !== lastNameInput.value || $[14] !== user) {
    payload = {
      id: user.id,
      name: {
        first: firstNameInput.value,
        last: lastNameInput.value
      },
      email: emailInput.value
    };
    t7 = !isEqual(user, payload) && stringFieldsAreFilled([firstNameInput.value, lastNameInput.value, emailInput.value]);
    $[11] = emailInput.value;
    $[12] = firstNameInput.value;
    $[13] = lastNameInput.value;
    $[14] = user;
    $[15] = payload;
    $[16] = t7;
  } else {
    payload = $[15];
    t7 = $[16];
  }
  const isEnabled = t7;
  let t8;
  if ($[17] !== isEnabled || $[18] !== payload || $[19] !== updateProfile) {
    t8 = async e => {
      e.preventDefault();
      ;
      try {
        if (!isEnabled) {
          return;
        }
        await updateProfile(payload);
      } catch (t9) {
        const e_0 = t9;
        const handleHttpError = makeHandleHttpError("PROFILE_ERROR");
        setError(handleHttpError(e_0));
      }
    };
    $[17] = isEnabled;
    $[18] = payload;
    $[19] = updateProfile;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handleSubmit = t8;
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = async e_1 => {
      e_1.preventDefault();
      setKey(increment);
    };
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const handleCancelForm = t9;
  let t10;
  if ($[22] !== destroyUser || $[23] !== router || $[24] !== user.email) {
    t10 = async () => {
      ;
      try {
        await destroyUser();
        posthog.capture(ph("profile_settings", "user", "deleted"));
        track("Delete Account", {
          email: user.email
        });
        router.replace("/register");
      } catch (t11) {
        const e_2 = t11;
        const handleHttpError_0 = makeHandleHttpError("PROFILE_ERROR");
        setError(handleHttpError_0(e_2));
      }
    };
    $[22] = destroyUser;
    $[23] = router;
    $[24] = user.email;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const handleDelete = t10;
  let t11;
  if ($[26] !== firstNameInput) {
    t11 = <TextFormGroup {...firstNameInput} />;
    $[26] = firstNameInput;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== lastNameInput) {
    t12 = <TextFormGroup {...lastNameInput} />;
    $[28] = lastNameInput;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== emailInput) {
    t13 = <TextFormGroup {...emailInput} />;
    $[30] = emailInput;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t11 || $[33] !== t12 || $[34] !== t13) {
    t14 = <div className="settings-profile__input-group">{t11}{t12}{t13}</div>;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== onOpen || $[37] !== user.id) {
    t15 = onOpen(user.id);
    $[36] = onOpen;
    $[37] = user.id;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t15) {
    t16 = <DeleteButton onClick={t15}>Delete Account</DeleteButton>;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <CancelButton type="reset">Cancel</CancelButton>;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  const t18 = !isEnabled;
  let t19;
  if ($[42] !== t18) {
    t19 = <div className="settings-profile__button-group">{t17}<PrimaryButton disabled={t18} type="submit">Update Profile</PrimaryButton></div>;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t16 || $[45] !== t19) {
    t20 = <div className="settings-profile__button-group">{t16}{t19}</div>;
    $[44] = t16;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== handleSubmit || $[48] !== key || $[49] !== t14 || $[50] !== t20) {
    t21 = <form className="settings-profile" key={key} onSubmit={handleSubmit} onReset={handleCancelForm}>{t14}{t20}</form>;
    $[47] = handleSubmit;
    $[48] = key;
    $[49] = t14;
    $[50] = t20;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== handleDelete) {
    t22 = <DeleteButton onClick={handleDelete}>Delete</DeleteButton>;
    $[52] = handleDelete;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== onCancel) {
    t23 = <CancelButton onClick={onCancel}>Cancel</CancelButton>;
    $[54] = onCancel;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== t22 || $[57] !== t23 || $[58] !== user.id) {
    t24 = <Alert id={user.id} title="Are you sure you want to delete your account?" message="This action is NOT reversible">{t22}{t23}</Alert>;
    $[56] = t22;
    $[57] = t23;
    $[58] = user.id;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== t21 || $[61] !== t24) {
    t25 = <>{t21}{t24}</>;
    $[60] = t21;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  return t25;
};
function _temp(s) {
  return s.data;
}
function _temp2(s_0) {
  return s_0.updateProfile;
}
function _temp3(s_1) {
  return s_1.destroy;
}